import {
	type Currencies,
	type InvestmentBenchmark,
	type InvestmentReference,
	EntityEditorControllerApiFactory,
} from "@eu.mdotm.api/sphere";
import { useApiGen } from "$root/api/hooks";
import { ReactQueryWrapperBase } from "$root/components/ReactQueryWrapper";
import type { MinimumDialogProps } from "$root/components/spawnable/type";
import { useDebouncedNameUniquenessChecker } from "$root/functional-areas/named-entities/uniqueness";
import { portfolioRepository } from "$root/functional-areas/portfolio/repository";
import { axiosExtract } from "$root/third-party-integrations/axios";
import { FormController } from "$root/third-party-integrations/react-hook-form";
import { FormFields } from "$root/ui-lib/form/FormFields";
import { valueByPath } from "$root/utils";
import { useQueryNoRefetch } from "$root/third-party-integrations/react-query/hooks";
import { InfoTooltip } from "$root/widgets-architecture/layout/WidgetsMapper/InfoTooltip";
import { zodResolver } from "@hookform/resolvers/zod";
import {
	type SelectOption,
	type SelectOptions,
	Button,
	FormField,
	Label,
	SubmitButton,
} from "@mdotm/shadcn/mdotm/components";
import { CircularProgressBar, Dialog, DialogFooter, Text, TooltipContent } from "@mdotm/shadcn/legacy/components";
import { Radio, RadioGroup } from "@mdotm/shadcn/mdotm/components";
import type { MaybePromise } from "@mdotm/utils/promise";
import { type SpawnResult, adaptAnimatedNodeProvider, spawn } from "@mdotm/shadcn/legacy/react-extensions";
import { noop } from "@mdotm/shadcn/legacy/utils";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { errorMessageNameRequired } from "./handler";

import type { TargetPortfolioManualCreationFormProps } from "./spawn-create-reference.types";
export type SubmitTargetPortfolioCreationDialogProps = {
	onAsyncSubmit(payload: Required<TargetPortfolioManualCreationFormProps>): MaybePromise<void>;
} & MinimumDialogProps;

function groupSelectOptions<T>(
	rows: Array<SelectOption<T> & { group?: { id: string; label?: string } | string }>,
): SelectOptions<T> {
	const byGroup = new Map<string, SelectOption<T>[]>();
	for (const { group, ...opt } of rows) {
		const key = typeof group === "string" ? group : (group?.label ?? group?.id ?? "");
		const arr = byGroup.get(key) ?? [];
		arr.push(opt as SelectOption<T>);
		byGroup.set(key, arr);
	}
	return [...byGroup.entries()];
}

const defaultManualCreationData: Partial<TargetPortfolioManualCreationFormProps> = {
	name: "",
	type: undefined,
	baseCurrency: "EUR",
	fixedWeights: false,
};

const targetPortfolioTypeOptions: SelectOption<"TARGET_INVESTMENT" | "BENCHMARK">[] = [
	{
		label: "Reference",
		value: "TARGET_INVESTMENT",
	},
	{
		label: "Benchmark",
		value: "BENCHMARK",
	},
];

function SubmitTargetPortfolioCreationDialog(props: SubmitTargetPortfolioCreationDialogProps) {
	const { show, onClose, onAnimationStateChange, onAsyncSubmit } = props;

	const { t } = useTranslation();
	const entityEditorApi = useApiGen(EntityEditorControllerApiFactory);

	const { checkIfNameIsAvailable, checkingNameUniqueness } = useDebouncedNameUniquenessChecker({
		isNameAvailableApi: portfolioRepository.isInvestmentNameAvailable,
		currentName: null,
	});

	const { control, formState, handleSubmit } = useForm<
		TargetPortfolioManualCreationFormProps,
		any,
		TargetPortfolioManualCreationFormProps
	>({
		defaultValues: defaultManualCreationData,
		resolver: zodResolver(
			z.object({
				name: z
					.string()
					.min(1, errorMessageNameRequired.REFERENCE)
					.refine((name) => checkIfNameIsAvailable(name), {
						message: t("MISSING_FIELD.NAME_NOT_AVAILABLE"),
					}),
				type: z.enum(["TARGET_INVESTMENT", "BENCHMARK"], {
					required_error: "Select a valid label",
					invalid_type_error: "Select a valid label",
				}),
				baseCurrency: z.string(),
				fixedWeights: z.boolean(),
			}),
		),
	});

	const selectableQuery = useQueryNoRefetch({
		queryKey: ["selectable", "currencies", "benchmarks"],
		queryFn: async () => {
			const selectable = await axiosExtract(entityEditorApi.getEditorNewSelectableMainInfo());

			const currencies = (selectable.availableCurrencies ?? []).map(
				(currency): SelectOption<Currencies> => ({
					label: currency ?? "",
					value: currency,
				}),
			);

			const benchmarks = groupSelectOptions<InvestmentBenchmark>(
				(selectable.availablePrimaryBenchmarks ?? []).map((el) => ({
					label: el.benchmarkName ?? "",
					value: { benchmarkIdentifier: el.benchmarkIdentifier, benchmarkType: el.benchmarkType },
					group: t(`INVESTMENT_REFERENCE_CATEGORIES.${el.benchmarkType!}`),
					disabled: !el.available,
				})),
			);

			const references = groupSelectOptions<InvestmentReference>(
				(selectable.availableInvestmentReferences ?? []).map((el) => ({
					label: el.name ?? "-",
					value: { referenceIdentifier: el.identifier, referenceType: el.type } satisfies InvestmentReference,
					group: t(`INVESTMENT_REFERENCE_CATEGORIES.${el.type!}`),
					disabled: !el.available,
				})),
			);

			return { currencies, benchmarks, references };
		},
		refetchOnMount: false,
		cacheTime: 1000 * 60 * 60 * 4,
	});

	return (
		<Dialog
			size="medium"
			noValidate
			show={show}
			onClose={onClose}
			header="Reference name"
			onAnimationStateChange={onAnimationStateChange}
			footer={
				<DialogFooter
					neutralAction={
						<Button palette="tertiary" data-qualifier="CompositionEditor/Modal/Cancel" onClick={onClose}>
							{t("BUTTON.CANCEL")}
						</Button>
					}
					primaryAction={
						<SubmitButton data-qualifier="CompositionEditor/Modal/Save" onClick={noop}>
							{t("BUTTON.GENERATE")}
						</SubmitButton>
					}
				/>
			}
			onSubmitAsync={async () => {
				await handleSubmit((payload) => onAsyncSubmit(payload))();
			}}
		>
			<div className="grid gap-4 transition-all ease-elastic">
				<FormFields.Text
					control={control}
					formState={formState}
					name="name"
					label="Name"
					data-qualifier="CompositionEditor/SaveDialog/Name"
					placeholder="Name"
					rightContent={
						checkingNameUniqueness ? <CircularProgressBar className="w-3" value="indeterminate" /> : undefined
					}
				/>

				<ReactQueryWrapperBase
					query={selectableQuery}
					loadingFallback={<CircularProgressBar style={{ width: "1rem" }} value="indeterminate" />}
				>
					{({ currencies }) => (
						<FormFields.Select
							control={control}
							formState={formState}
							name="baseCurrency"
							label="Base currency"
							placeholder={t("PORTFOLIOS.PORTFOLIO_CURRENCY_PLACEHOLDER")}
							style={{ width: "100%" }}
							options={currencies}
							data-qualifier="CompositionEditor/SaveDialog/Currency"
							enableSearch
						/>
					)}
				</ReactQueryWrapperBase>

				<FormFields.Select
					control={control}
					formState={formState}
					name="type"
					label="Label"
					placeholder="Select label"
					style={{ width: "100%" }}
					options={targetPortfolioTypeOptions}
					data-qualifier="CompositionEditor/SaveDialog/Type"
					triggerDataAttrs={{ "data-qualifier": "CompositionEditor/SaveDialog/Type/Trigger" }}
				/>

				<FormField
					label={({ htmlFor }) => (
						<div className="flex items-center w-fit mb-px space-x-2">
							<Label htmlFor={htmlFor}>Weighting method</Label>
							<InfoTooltip align="startToEnd">
								<TooltipContent>
									<Text type="Body/M/Book">
										Drifting weights adjust daily based on market value changes over time, while Fixed weights remain
										unchanged until the next rebalancing.
									</Text>
								</TooltipContent>
							</InfoTooltip>
						</div>
					)}
					error={(valueByPath(formState.errors, "fixedWeights") as { message?: string })?.message}
				>
					{(fieldProps) => (
						<FormController
							control={control}
							name="fixedWeights"
							render={({ field: controllerProps }) => (
								<RadioGroup {...controllerProps} {...fieldProps} onChange={controllerProps.onChange}>
									<div className="flex flex-row flex-wrap gap-4">
										<Radio value={false} data-qualifier="CompositionEditor/SaveDialog/weightBehavior(Drifting)">
											Drifting
										</Radio>

										<Radio value data-qualifier="CompositionEditor/SaveDialog/weightBehavior(Fixed)">
											Fixed
										</Radio>
									</div>
								</RadioGroup>
							)}
						/>
					)}
				</FormField>
			</div>
		</Dialog>
	);
}

export type SpawnSubmitTargetPortfolioCreationDialogProps = Omit<
	SubmitTargetPortfolioCreationDialogProps,
	"show" | "onClose"
>;
export function spawnSubmitTargetPortfolioCreationDialog(
	props: SpawnSubmitTargetPortfolioCreationDialogProps,
): SpawnResult<void> {
	return spawn<void>(
		adaptAnimatedNodeProvider(({ show, resolve, onHidden }) => (
			<SubmitTargetPortfolioCreationDialog
				{...props}
				show={show}
				onAnimationStateChange={(state) => state === "hidden" && onHidden()}
				onClose={() => resolve()}
				onAsyncSubmit={async (payload) => {
					await props.onAsyncSubmit(payload);
					resolve();
				}}
			/>
		)),
	);
}
